import { Button, Checkbox, Tooltip } from "@chakra-ui/react";
import React, { useEffect, useMemo, useState } from "react";
import {
  useFilters,
  usePagination,
  useRowSelect,
  useSortBy,
  useTable,
} from "react-table";
import { ArrowDownIcon, DownloadIcon } from "../../../Assets/images/svgs";
import { ellipsisify } from "../../../Library/helpers";
import "./style.css";
import { Title } from "../../Atoms";

const ReactTable = ({ data, columns, showOverlay, showSelection, showSearch, onRowSelect, pageSize=5, heading, buttonName, buttonHandler=() => {}, showSelectAll=true, checkboxLabel, selectedRows = [], showDownload, downloadHandler=() => {} }) => {
  const [ pagintionInputVal, setPagintionInputVal ] = useState(1);

  const filterTypes = React.useMemo(
    () => ({
      text: (rows, id, filterValue) => {
        return rows.filter((row) => {
          let rowValue = row.values[id];
          return rowValue !== undefined
            ? String(rowValue).toLowerCase().includes(String(filterValue).toLowerCase())
            : true;
        });
      },
    }),
    []
  );

  function DefaultColumnFilter({ column: { filterValue, setFilter } }) {

    return (
      <input
        value={filterValue || ""}
        onChange={(e) => {
          setFilter(e.target.value || undefined);
        }}
        placeholder={"Search"}
        className="table-search-input"
      />
    );
  }

  const defaultColumn = React.useMemo(
    () => ({
      Filter: DefaultColumnFilter,
    }),
    []
  );

  const IndeterminateCheckbox = React.forwardRef(
    ({ indeterminate, ...rest }, ref) => {
      const defaultRef = React.useRef()
      const resolvedRef = ref || defaultRef

      React.useEffect(() => {
        resolvedRef.current.indeterminate = indeterminate
      }, [resolvedRef, indeterminate])

      rest["isChecked"] = rest.checked;
      return (
        <>
          <Checkbox className="select-row-input" ref={resolvedRef} {...rest} ></Checkbox>
        </>
      )
    }
  )

  const selectedRowIds = useMemo(() => {
    const selectedIds = {};
    data.forEach((row, index) => {
      if (selectedRows.includes(row.idx)) {
        selectedIds[index] = true;
      }
    });
    return selectedIds;
  }, [data, selectedRows]);

  const { getTableProps, getTableBodyProps, headerGroups, page, prepareRow, selectedFlatRows, state: { pageIndex }, pageCount, canPreviousPage, canNextPage, nextPage, previousPage, gotoPage } = useTable(
    {
      columns,
      data,
      defaultColumn,
      filterTypes,
      initialState: { pageSize: pageSize, selectedRowIds: selectedRowIds },
    },
    useFilters,
    useSortBy,
    usePagination,
    useRowSelect,
    hooks => {
      if(!showSelection) return;
      hooks.visibleColumns.push(columns => [
        {
          id: 'selection',
          type: 'select',
          Header: ({ getToggleAllPageRowsSelectedProps }) => {
            return showSelectAll
            ? ( <label className="checkbox-header-label">
                 <IndeterminateCheckbox {...getToggleAllPageRowsSelectedProps()} />
                 { checkboxLabel ? <p className="show-selection-label">{checkboxLabel}</p> : null }
                </label>
              )
            : null;
          },
          Cell: ({ row }) => (
            <div>
              {row.original.firstCol ? row.original.firstCol : <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />}
            </div>
          ),
        },
        ...columns,
      ])
    },
  );

  useEffect(() => {
    if (onRowSelect && typeof onRowSelect === 'function')
      onRowSelect(false, selectedFlatRows);
  }, [onRowSelect, selectedFlatRows]);

  const manageSelectedRows = () => {
    if (!onRowSelect) return;
    if(data.length === selectedFlatRows.length) onRowSelect(true);
    else onRowSelect(false, selectedFlatRows)
  }

  const handlePaginationInputChange = (e) => {
    const value = e.target.value;
    setPagintionInputVal(value);

    if (value) {
      const page = Number(value) - 1;
      if (!isNaN(page) && page >= 0 && page < pageCount) {
        gotoPage(page);
      }
    }
  };

  useEffect(() => {
    setPagintionInputVal(pageIndex + 1);
  }, [ pageIndex ])

  return (
    <div>
      <div className={'data-table-container'}>
        {
          ( heading || buttonName ) ? <div className="data-table-header">
            {heading ? <Title>{heading}</Title> : null}
            {buttonName ? <Button variant={"solid"} onClick={buttonHandler}>{buttonName}</Button> : null}
            {showDownload ? <DownloadIcon className="down-icon" onClick={downloadHandler}/> : null}
          </div> : null
        }
        <table className="data-table" {...getTableProps()}>
          <thead onClick={manageSelectedRows}>
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column, idx) => (
                  <th className={`table-heading-cell ${column.isSorted ? "align-icon" : ""}`} {...column.getHeaderProps(column.getSortByToggleProps())}>
                    <Tooltip label={`${column.Header.length > 12 ? column.Header : ""}`}>
                      <div>
                        {(idx || idx === 0) ? ellipsisify(column.Header, 12) : column.render("Header")}
                        <span>
                          {column.isSorted
                            ? column.isSortedDesc
                              ? <ArrowDownIcon className="sorted-icon" />
                              : <ArrowDownIcon className="sorted-icon rotate-arrow" />
                            : ''}
                        </span>
                      </div>
                    </Tooltip>
                  </th>
                ))}
              </tr>
            ))}
            {showSearch && headerGroups.map((headerGroup) => (
              <tr className="table-filter" {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <th {...column.getHeaderProps()}>
                    <div>{column.canFilter ? column.render("Filter") : null}</div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {page.length ? page.map((row, i) => {
              prepareRow(row);
              return (
                <tr {...row.getRowProps()}>
                  {row.cells.map((cell) => {
                    return (
                      <td className="table-data-cell" {...cell.getCellProps()}>
                        { cell.render("Cell") }
                      </td>
                    )
                  })}
                </tr>
              );
            }) : <tr className="no-data-avilable"><td colSpan={99}>No Data Available</td></tr>}
          </tbody>
        </table>
      </div>

      {showOverlay && <div className="popover-overlay"></div>}

      {
        pageCount > 1 ? <div className="table-pagination">
          <Button variant="ghost" onClick={() => previousPage()} disabled={!canPreviousPage}>
            Previous
          </Button>
          <div>
            <input
              type="number"
              className="pagination-input"
              value={pagintionInputVal}
              onChange={handlePaginationInputChange}
            />
            &nbsp;&nbsp;
            <span>{ pageIndex + 1 }/{ pageCount }</span>
          </div>
          <Button variant="ghost" onClick={() => nextPage()} disabled={!canNextPage}>
            Next
          </Button>
        </div> : null
      }
    </div>
  );
};

export default ReactTable;
